import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useUserData } from '../contexts/UserDataContext';
import Button from '../components/Button';
import Slider from '../components/Slider';

const HomeContainer = styled.div`
  min-height: 80vh;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: ${props => props.theme.spacing.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.darkBlue}, ${props => props.theme.colors.blue});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  letter-spacing: -1px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.darkGray};
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ExpectedPensionSection = styled.section`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.darkBlue};
  font-size: 1.8rem;
  font-weight: 700;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Button)`
  font-size: 1.2rem;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.md};
  min-width: 300px;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  transition: transform 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
`;

const InfoSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.orange}, ${props => props.theme.colors.red});
  color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: ${props => props.theme.spacing.md};
    font-weight: 700;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.7;
    opacity: 0.95;
  }
`;

const HomePage: React.FC = () => {
  const { userData, setUserData } = useUserData();
  const navigate = useNavigate();
  const [expectedPension, setExpectedPension] = useState<number>(userData.expectedPension);

  const handleStartSimulation = () => {
    if (expectedPension > 0) {
      navigate('/simulation');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <HomeContainer>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <HeroSection>
            <HeroTitle variants={itemVariants}>
              ZUS Inteligentny Kalkulator (ZUSik)
            </HeroTitle>
            <HeroSubtitle variants={itemVariants}>
              Sprawdź, jaką emeryturę możesz otrzymać w przyszłości. 
              Nasze narzędzie edukacyjne pomoże Ci zaplanować przyszłość finansową.
            </HeroSubtitle>
          </HeroSection>

          <motion.div variants={itemVariants}>
            <ExpectedPensionSection>
              <SectionTitle>Jaką emeryturę chciałbyś otrzymywać?</SectionTitle>
              <InputGroup>
                <Slider
                  label="Oczekiwana wysokość emerytury"
                  value={expectedPension}
                  onChange={(value) => {
                    setExpectedPension(value);
                    setUserData({ expectedPension: value });
                  }}
                  min={500}
                  max={15000}
                  step={100}
                  unit=" PLN"
                />
              </InputGroup>
              <CTAButton
                onClick={handleStartSimulation}
                disabled={expectedPension <= 0}
                variant="primary"
                size="large"
              >
                Rozpocznij symulację
              </CTAButton>
            </ExpectedPensionSection>
          </motion.div>

          <motion.div variants={itemVariants}>
            <InfoSection>
              <h3>🎯 Ciekawostka</h3>
              <p>
                Średnia emerytura w Polsce w 2024 roku wynosi 2 800 PLN, 
                co stanowi około 60% średniego wynagrodzenia brutto. 
                Aby otrzymać pełną emeryturę, mężczyźni muszą przepracować minimum 25 lat, 
                a kobiety 20 lat.
              </p>
            </InfoSection>
          </motion.div>
        </motion.div>
      </Container>
    </HomeContainer>
  );
};

export default HomePage;
